import React, { useState, useEffect } from "react";
import { applicationsApi } from "../../../services/api";
import {
  ApplicationStatusDto,
  FewQuestionsContentProps,
} from "../../../common/types";
import {
  DropdownField,
  InputAmountField,
  InputField,
  LabelledToggleSwitch,
  ReadOnlyField,
  SectionHeader,
} from "../../../common/helperMethods";
import { getStorage } from "../../../common/storageHelper";
import { customerInfoIdValue } from "../../../common/helperConstants";
import { toast } from "react-toastify";
import {
  CustomerInfoIdNotFoundText,
  DataSubmittedSuccessfullyText,
  SomethingWentWrongText,
} from "../../../common/messageContainer";
import CircleSpinner from "../../../common/loader/circleSpinner";

const FewQuestionsContent: React.FC<FewQuestionsContentProps> = ({
  onNextStep,
  mainContentClassName,
  markStepAsCompleted,
  currentStepId,
}) => {
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const infoId = getStorage(customerInfoIdValue);
  // API data
  const [questionaryData, setQuestionaryData] =
    useState<ApplicationStatusDto | null>(null);
  const [questionaryRequestData, setQuestionaryRequestData] =
    useState<any>(null);

  // Form states
  const [applicantAccurate, _setApplicantAccurate] = useState(true);
  const [coApplicantAccurate, _setCoApplicantAccurate] = useState(true);
  const [coApplicantRelationship, _setCoApplicantRelationship] =
    useState("Spouse");
  const [contractAccurate, _setContractAccurate] = useState(true);
  const [downPayment, _setDownPayment] = useState("");
  const [vehicleColor, _setVehicleColor] = useState("");
  const [hasPossession, _setHasPossession] = useState(true);
  const [possessionReason, _setPossessionReason] = useState("");
  const [hasMechanicalIssues, _setHasMechanicalIssues] = useState(false);
  const [mechanicalIssueDetails, _setMechanicalIssueDetails] = useState("");
  const [dealerFixedIssues, _setDealerFixedIssues] = useState(false);
  const [isPrimaryDriver, _setIsPrimaryDriver] = useState(true);
  const [wantsPaperless, _setWantsPaperless] = useState(true);
  const [wantsVCEasyPay, _setWantsVCEasyPay] = useState(true);

  const handleInteraction = () => {
    markStepAsCompleted(currentStepId);
  };

  // Wrapper functions to call handleInteraction
  const setApplicantAccurate = (val: boolean) => {
    _setApplicantAccurate(val);
  };
  const setCoApplicantAccurate = (val: boolean) => {
    _setCoApplicantAccurate(val);
  };
  const setCoApplicantRelationship = (val: string) => {
    _setCoApplicantRelationship(val);
  };
  const setContractAccurate = (val: boolean) => {
    _setContractAccurate(val);
  };

  const setDownPayment = (val: string) => {
    _setDownPayment(val);
    setQuestionaryRequestData((prev: any) => ({
      ...prev,
      downPayment: val ? parseFloat(val) : null,
    }));
  };

  const setVehicleColor = (val: string) => {
    _setVehicleColor(val);
    setQuestionaryRequestData((prev: any) => ({ ...prev, vehicleColor: val }));
  };

  const setHasPossession = (val: boolean) => {
    _setHasPossession(val);
    setQuestionaryRequestData((prev: any) => ({ ...prev, hasPossession: val }));
  };

  const setPossessionReason = (val: string) => {
    _setPossessionReason(val);
  };

  const setHasMechanicalIssues = (val: boolean) => {
    _setHasMechanicalIssues(val);
    setQuestionaryRequestData((prev: any) => ({
      ...prev,
      hasMajorMechanicalIssues: val,
    }));
  };

  const setMechanicalIssueDetails = (val: string) => {
    _setMechanicalIssueDetails(val);
    setQuestionaryRequestData((prev: any) => ({
      ...prev,
      issueDescription: val,
    }));
  };

  const setDealerFixedIssues = (val: boolean) => {
    _setDealerFixedIssues(val);
    setQuestionaryRequestData((prev: any) => ({
      ...prev,
      dealerFixedIssues: val,
    }));
  };

  const setIsPrimaryDriver = (val: boolean) => {
    _setIsPrimaryDriver(val);
  };
  const setWantsPaperless = (val: boolean) => {
    _setWantsPaperless(val);
  };

  const setWantsVCEasyPay = (val: boolean) => {
    _setWantsVCEasyPay(val);
  };

  const relationships = [
    { value: "Aunt", label: "Aunt" },
    { value: "Brother", label: "Brother" },
    { value: "Brother-In-Law", label: "Brother-In-Law" },
    { value: "Cousin", label: "Cousin" },
    { value: "Daughter", label: "Daughter" },
    { value: "Domestic Partner", label: "Domestic Partner" },
    { value: "Father", label: "Father" },
    { value: "Friend", label: "Friend" },
    { value: "Grandfather", label: "Grandfather" },
    { value: "Grandmother", label: "Grandmother" },
    { value: "Mother", label: "Mother" },
    { value: "Nephew", label: "Nephew" },
    { value: "Niece", label: "Niece" },
    { value: "Sister", label: "Sister" },
    { value: "Sister-In-Law", label: "Sister-In-Law" },
    { value: "Son", label: "Son" },
    { value: "Spouse", label: "Spouse" },
    { value: "Uncle", label: "Uncle" },
  ];

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);

      const custInfo: ApplicationStatusDto =
        await applicationsApi.getApplication(infoId);

      if (!custInfo || typeof custInfo !== "object") {
        setIsLoader(false);
        return;
      }

      setQuestionaryData(custInfo);

      // Initialize request data with values from API response (if available) or defaults
      const initialRequestData = {
        vehicleColor: custInfo?.questions?.vehicleColor || "",
        hasPossession: custInfo?.questions?.hasPossession ?? true,
        hasMajorMechanicalIssues:
          custInfo?.questions?.hasMajorMechanicalIssues ?? false,
        issueDescription: custInfo?.questions?.issueDescription || null,
        dealerFixedIssues: custInfo?.questions?.dealerFixedIssues ?? false,
        downPayment: custInfo?.contract?.downPayment || null,
      };

      setQuestionaryRequestData(initialRequestData);

      // Set local form states from fetched data
      if (
        custInfo?.questions?.isApplicantInfo !== undefined &&
        custInfo?.questions?.isApplicantInfo !== null
      ) {
        _setApplicantAccurate(custInfo.questions.isApplicantInfo);
      }
      if (
        custInfo?.questions?.isCoApplicantInfo !== undefined &&
        custInfo?.questions?.isCoApplicantInfo !== null
      ) {
        _setCoApplicantAccurate(custInfo.questions.isCoApplicantInfo);
      }
      if (custInfo?.questions?.coApplicantRelationship) {
        _setCoApplicantRelationship(custInfo.questions.coApplicantRelationship);
      }
      if (
        custInfo?.questions?.isContractInfo !== undefined &&
        custInfo?.questions?.isContractInfo !== null
      ) {
        _setContractAccurate(custInfo.questions.isContractInfo);
      }
      if (
        custInfo?.questions?.isPrimaryDriver !== undefined &&
        custInfo?.questions?.isPrimaryDriver !== null
      ) {
        _setIsPrimaryDriver(custInfo.questions.isPrimaryDriver);
      }
      if (
        custInfo?.questions?.isPaperlessBilling !== undefined &&
        custInfo?.questions?.isPaperlessBilling !== null
      ) {
        _setWantsPaperless(custInfo.questions.isPaperlessBilling);
      }
      if (
        custInfo?.questions?.interestInVCEasyPay !== undefined &&
        custInfo?.questions?.interestInVCEasyPay !== null
      ) {
        _setWantsVCEasyPay(custInfo.questions.interestInVCEasyPay);
      }
      if (custInfo?.questions?.vehicleColor) {
        _setVehicleColor(custInfo.questions.vehicleColor);
      }
      if (custInfo?.questions?.hasPossession !== undefined) {
        _setHasPossession(custInfo.questions.hasPossession);
      }
      if (custInfo?.questions?.possessionReason) {
        _setPossessionReason(custInfo.questions.possessionReason);
      }
      if (custInfo?.questions?.hasMajorMechanicalIssues !== undefined) {
        _setHasMechanicalIssues(custInfo.questions.hasMajorMechanicalIssues);
      }
      if (custInfo?.questions?.issueDescription) {
        _setMechanicalIssueDetails(custInfo.questions.issueDescription);
      }
      if (custInfo?.questions?.dealerFixedIssues !== undefined) {
        _setDealerFixedIssues(custInfo.questions.dealerFixedIssues);
      }

      // Set initial down payment if available
      if (custInfo?.contract?.downPayment) {
        _setDownPayment(custInfo.contract.downPayment.toString());
      }
    } catch (err: any) {
      toast.error(
        err.response?.data?.message || err.message || SomethingWentWrongText,
      );
    } finally {
      setIsLoader(false);
    }
  };

  const trimOrNull = (val: any) =>
    typeof val === "string" && val.trim() === ""
      ? null
      : typeof val === "string"
        ? val.trim()
        : (val ?? null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!infoId) {
      toast.error(CustomerInfoIdNotFoundText);
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
      return;
    }

    try {
      setIsLoader(true);

      const questionnaireRequestData = {
        isApplicantInfo: applicantAccurate,
        ...(questionaryData?.coApplicant?.firstName && {
          isCoApplicantInfo: coApplicantAccurate,
          coApplicantRelationship: trimOrNull(coApplicantRelationship),
        }),
        isContractInfo: contractAccurate,
        isPrimaryDriver,
        isPaperlessBilling: wantsPaperless,
        interestInVCEasyPay: wantsVCEasyPay,
        vehicleColor: trimOrNull(
          questionaryRequestData?.vehicleColor || vehicleColor,
        ),
        hasPossession: trimOrNull(
          questionaryRequestData?.hasPossession ?? hasPossession,
        ),
        hasMajorMechanicalIssues: trimOrNull(
          questionaryRequestData?.hasMajorMechanicalIssues ??
            hasMechanicalIssues,
        ),
        issueDescription: trimOrNull(
          (questionaryRequestData?.hasMajorMechanicalIssues ??
            hasMechanicalIssues)
            ? questionaryRequestData?.issueDescription || mechanicalIssueDetails
            : null,
        ),
        dealerFixedIssues:
          (questionaryRequestData?.hasMajorMechanicalIssues ??
          hasMechanicalIssues)
            ? (questionaryRequestData?.dealerFixedIssues ?? dealerFixedIssues)
            : false,
        downPayment: trimOrNull(
          questionaryRequestData?.downPayment ??
            (downPayment ? parseFloat(downPayment) : null),
        ),
        possessionReason: trimOrNull(!hasPossession ? possessionReason : null),
      };

      var response = await applicationsApi.saveQuestionnaire(
        infoId,
        questionnaireRequestData,
      );

      if (response?.success) {
        handleInteraction();
        toast.success(DataSubmittedSuccessfullyText);
        setTimeout(() => {
          onNextStep();
        }, 500);
      } else {
        toast.error(SomethingWentWrongText);
      }
    } catch (err: any) {
      toast.error(SomethingWentWrongText);
    } finally {
      setIsLoader(false);
    }
  };

  // Format phone number
  const formatPhone = (phone: string | null | undefined) => {
    if (!phone) return "N/A";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`;
    }
    return phone;
  };

  // Format date
  const formatDate = (date: string | null | undefined) => {
    if (!date) return "N/A";
    try {
      const d = new Date(date);
      return d.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
    } catch {
      return date;
    }
  };

  // Format currency
  const formatCurrency = (amount: number | null | undefined) => {
    if (amount === null || amount === undefined) return "N/A";
    return `$${amount.toFixed(2)}`;
  };

  const sectionTitleClass =
    "text-xl lg:text-2xl font-semibold text-[#09517d] mb-3";
  const cardContentClass = "p-4 sm:p-6";

  // If we don't have invite data, show an error
  if (!questionaryData || questionaryData.dealLaneQuestions) {
    return (
      <main className={mainContentClassName || "w-full"}>
        <div
          className={`${isLoader === true ? "loaderGrey" : "loaderDisplay"}`}
        >
          <CircleSpinner />
        </div>
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg h-full">
          <div className="text-center text-gray-600">
            {isLoader ? (
              <div className="text-center text-gray-600">Loading Data...</div>
            ) : !questionaryData ? (
              <>
                <p className="text-xl font-semibold mb-2">
                  No Application Data Found
                </p>
                <p>Unable to load application information.</p>
              </>
            ) : (
              <>
                <p className="text-xl font-semibold mb-2">
                  You have already submitted your Questions. Please proceed to
                  the next step.
                </p>
                <div className="mt-8 text-right md:block">
                  <button
                    type="button"
                    onClick={onNextStep}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-base lg:text-[1.2rem] lg:leading-[2rem]"
                    aria-label="Submit questions and proceed"
                  >
                    Submit & Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={mainContentClassName || "w-full"}>
      <div className={`${isLoader === true ? "loaderGrey" : "loaderDisplay"}`}>
        <CircleSpinner />
      </div>
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg h-full">
        <h1 className="text-2xl font-semibold text-[#09517d] mb-6 text-center sm:text-left">
          Can you update us on a few items?
        </h1>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 xl:grid-cols-2 md:gap-x-8 gap-y-8">
            {/* Applicant Section */}
            <div>
              <h2 className={sectionTitleClass}>Applicant</h2>
              <section className="bg-white rounded-lg shadow">
                <SectionHeader
                  title="Is this info accurate?"
                  toggleId="applicant-toggle"
                  toggleChecked={applicantAccurate}
                  onToggleChange={setApplicantAccurate}
                />
                <div className={cardContentClass}>
                  <ReadOnlyField
                    label="Name"
                    value={
                      questionaryData?.customer?.firstName &&
                      questionaryData?.customer?.lastName
                        ? `${questionaryData.customer.firstName} ${questionaryData.customer.lastName}`
                        : "N/A"
                    }
                  />
                  <ReadOnlyField
                    label="Date of Birth"
                    value={formatDate(questionaryData?.customer?.dob)}
                  />
                  <ReadOnlyField
                    label="Cell Phone"
                    value={formatPhone(questionaryData?.customer?.cellPhone)}
                  />
                  <ReadOnlyField
                    label="SSN"
                    value={
                      questionaryData?.customer?.ssnLast4
                        ? `***-**-${questionaryData.customer.ssnLast4}`
                        : "N/A"
                    }
                  />
                  <ReadOnlyField
                    label="Home Phone"
                    value={formatPhone(questionaryData?.customer?.homePhone)}
                  />
                  <ReadOnlyField
                    label="Email"
                    value={questionaryData?.customer?.email || "N/A"}
                  />
                </div>
              </section>
            </div>

            {/* Co-Applicant Section */}
            {questionaryData?.coApplicant?.firstName && (
              <div>
                <h2 className={sectionTitleClass}>Co Applicant</h2>
                <section className="bg-white rounded-lg shadow">
                  <SectionHeader
                    title="Is this info accurate?"
                    toggleId="coapplicant-toggle"
                    toggleChecked={coApplicantAccurate}
                    onToggleChange={setCoApplicantAccurate}
                  />
                  <div className={cardContentClass}>
                    <ReadOnlyField
                      label="Name"
                      value={`${questionaryData.coApplicant?.firstName} ${
                        questionaryData.coApplicant?.lastName || ""
                      }`}
                    />
                    <ReadOnlyField
                      label="Date of Birth"
                      value={formatDate(questionaryData?.coApplicant?.dob)}
                    />
                    <ReadOnlyField
                      label="Cell Phone"
                      value={formatPhone(
                        questionaryData?.coApplicant?.cellPhone,
                      )}
                    />
                    <ReadOnlyField
                      label="SSN"
                      value={
                        questionaryData?.coApplicant?.ssnLast4
                          ? `***-**-${questionaryData.coApplicant?.ssnLast4}`
                          : "N/A"
                      }
                    />
                    <ReadOnlyField
                      label="Home Phone"
                      value={formatPhone(
                        questionaryData?.coApplicant?.homePhone,
                      )}
                    />
                    <ReadOnlyField
                      label="Email"
                      value={questionaryData?.coApplicant?.email || "N/A"}
                    />
                    <DropdownField
                      label="What is the relationship to Applicant?"
                      id="coAppRelationship"
                      value={coApplicantRelationship || ""}
                      onChange={setCoApplicantRelationship}
                      options={relationships}
                      disabled={!coApplicantAccurate}
                    />
                  </div>
                </section>
              </div>
            )}

            {/* Contract Section */}
            <div>
              <h2 className={sectionTitleClass}>Contract</h2>
              <section className="bg-white rounded-lg shadow">
                <SectionHeader
                  title="Is this info accurate?"
                  toggleId="contract-toggle"
                  toggleChecked={contractAccurate}
                  onToggleChange={setContractAccurate}
                />
                <div className={cardContentClass}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-0 mb-3">
                    <ReadOnlyField
                      label="Contract Date"
                      value={formatDate(
                        questionaryData?.contract?.contractDate,
                      )}
                    />
                    <ReadOnlyField
                      label="First Due Date"
                      value={formatDate(
                        questionaryData?.contract?.firstDueDate,
                      )}
                    />
                    <ReadOnlyField
                      label="Term"
                      value={
                        questionaryData?.contract?.termMonths?.toString() ||
                        "N/A"
                      }
                    />
                    <ReadOnlyField
                      label="Payment Amount"
                      value={
                        formatCurrency(
                          questionaryData?.contract?.paymentAmount,
                        ) || "N/A"
                      }
                    />
                  </div>
                  <InputAmountField
                    label="How much was the down payment?"
                    id="downPayment"
                    value={downPayment}
                    onChange={setDownPayment}
                    placeholder="Down Payment Amount"
                    type="number"
                    required
                  />
                </div>
              </section>
            </div>

            {/* Vehicle Section */}
            <div>
              <h2 className={sectionTitleClass}>Vehicle</h2>
              <section className="bg-white rounded-lg shadow">
                <div className="bg-[#09517d] text-white p-3 rounded-t-lg flex flex-col sm:flex-row justify-between items-center text-center sm:text-left">
                  <h3 className="text-base leading-4 font-semibold">
                    {questionaryData?.vehicleInfo ||
                      "Vehicle Information Not Available"}
                  </h3>
                  {questionaryData?.vehicle?.mileage && (
                    <span className="text-sm leading-4 sm:text-base sm:leading-4 mt-1 sm:mt-0">
                      Mileage:{" "}
                      {questionaryData.vehicle?.mileage?.toLocaleString()}
                    </span>
                  )}
                </div>
                <div className={cardContentClass + " space-y-4"}>
                  <InputField
                    label="What color is the vehicle?"
                    id="vehicleColor"
                    value={vehicleColor}
                    onChange={setVehicleColor}
                    placeholder="e.g., Red, Blue, Silver"
                    maxLength={20}
                    required
                  />
                  <LabelledToggleSwitch
                    label="Do you have possession of the vehicle?"
                    id="hasPossessionToggle"
                    checked={hasPossession}
                    onChange={setHasPossession}
                  />
                  {!hasPossession && (
                    <InputField
                      label="Why don't you have possession?"
                      id="possessionReason"
                      value={possessionReason}
                      onChange={setPossessionReason}
                      placeholder="Info Needed"
                      required={!hasPossession}
                      maxLength={500}
                    />
                  )}
                  <LabelledToggleSwitch
                    label="Are there any known major mechanical issues?"
                    id="hasMechanicalIssuesToggle"
                    checked={hasMechanicalIssues}
                    onChange={setHasMechanicalIssues}
                  />
                  {hasMechanicalIssues && (
                    <>
                      <InputField
                        label="What is the issue?"
                        id="mechanicalIssueDetails"
                        value={mechanicalIssueDetails}
                        onChange={setMechanicalIssueDetails}
                        placeholder="Describe issue"
                        required={hasMechanicalIssues}
                        maxLength={500}
                      />
                      <LabelledToggleSwitch
                        label="Has dealer fixed the mechanical issues?"
                        id="dealerFixedIssuesToggle"
                        checked={dealerFixedIssues}
                        onChange={setDealerFixedIssues}
                      />
                    </>
                  )}
                </div>
              </section>
            </div>

            {/* Questions Section */}
            <div>
              <h2 className={sectionTitleClass}>Questions</h2>
              <section className="bg-white rounded-lg shadow">
                <div className={cardContentClass + " space-y-0"}>
                  <LabelledToggleSwitch
                    label="Are you the primary driver?"
                    id="isPrimaryDriverToggle"
                    checked={isPrimaryDriver}
                    onChange={setIsPrimaryDriver}
                  />
                  <LabelledToggleSwitch
                    label="Are you interested in Paperless Billing?"
                    id="wantsPaperlessToggle"
                    checked={wantsPaperless}
                    onChange={setWantsPaperless}
                  />
                  <LabelledToggleSwitch
                    label="Are you interested in VC Easy Pay?"
                    id="wantsVCEasyPayToggle"
                    checked={wantsVCEasyPay}
                    onChange={setWantsVCEasyPay}
                  />
                </div>
              </section>
            </div>
          </div>

          <div className="mt-8 text-right md:block">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-base lg:text-[1.2rem] lg:leading-[2rem]"
              aria-label="Submit questions and proceed"
            >
              Submit & Next
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default FewQuestionsContent;
